import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Header, Table, TenantIcon } from '../CommonComponents';
import { Container, Paper } from '../StyledComponents';

import { useDispatch, useSelector } from '../../redux/hooks';
import { fetchAllTenants, selectAllTenants, selectFetchingAllTenants } from '../../redux/actions/tenants';
import { fetchSettableMetadataForTenant, selectSettableMetadataForTenant } from '../../redux/actions/metadata';
import { selectTableOptions } from '../../redux/actions/tables';
import { isFairtransportMember } from '../../utils/metadata';
import { TenantForListType } from '../../types';
import { formatSubscriptionDate } from '../../utils/parsing';
import { carrierNameCapitalized } from '../../env';

const renderTenantIcon = (
    { isCarrier, activeSubscription, subscriptionEndDate }:
        { isCarrier: boolean, activeSubscription: boolean, subscriptionEndDate: string }
) => (
    <TenantIcon
        isCarrier={isCarrier}
        activeSubscription={activeSubscription}
        subscriptionEndDate={subscriptionEndDate}
        badgeSize={20}
        top={-4}
        right={22}
    />
);

export default function Tenants() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const tenants = useSelector(selectAllTenants());
    const settableMetadata = useSelector(selectSettableMetadataForTenant());
    const isFetching = useSelector(selectFetchingAllTenants());
    const tableOptions = useSelector(selectTableOptions('tenants'));

    const filterValues = tableOptions?.filterValues as { [key: string]: string | number | boolean } | undefined;

    const isCarrierFilterNo = React.useMemo(() => {
        return filterValues?.['isCarrier-equals'] === false
            || filterValues?.['isCarrier-equals'] === 'false';
    }, [filterValues]);

    React.useEffect(() => {
        dispatch(fetchAllTenants());
        dispatch(fetchSettableMetadataForTenant());
    }, [dispatch]);

    const onRowClick = React.useCallback((tenant: { id: number }) => {
        navigate(`/tenants/${tenant.id}`);
    }, [navigate]);

    const columns = [{
        name: 'Typ',
        key: 'isCarrier',
        getValue: renderTenantIcon,
        getSortValue: (t: TenantForListType) => t.isCarrier
    }, {
        name: carrierNameCapitalized,
        key: 'isCarrier',
        type: 'bool' as 'bool',
        applyDirectly: true,
        isHidden: true,
        canFilter: true
    }, {
        name: 'Namn',
        key: 'name',
        type: 'string' as 'string',
        canSearch: true
    }, {
        name: 'Org. nummer',
        key: 'orgNumber',
        canSearch: true
    }, {
        name: 'Aktivt abonnemang',
        key: 'activeSubscription',
        type: 'bool' as 'bool',
        getValue: (t: TenantForListType) => t.isCarrier && t.activeSubscription,
        canFilter: !isCarrierFilterNo,
        isHidden: true,
        linkedFilters: [{ filterKey: 'isCarrier-equals', value: true }]
    }, {
        name: 'Tidigare abonnemang',
        key: 'previousSubscription',
        type: 'bool' as 'bool',
        getValue: (t: TenantForListType) => t.isCarrier && !t.activeSubscription && t.subscriptionEndDate !== null,
        canFilter: !isCarrierFilterNo,
        isHidden: true,
        linkedFilters: [
            { filterKey: 'isCarrier-equals', value: true },
            { filterKey: 'activeSubscription-equals', whenValue: false, value: false }
        ]
    }, {
        name: 'Accepterad',
        key: 'isAccepted',
        type: 'bool' as 'bool',
        canFilter: true
    }, {
        name: 'Avvisad',
        key: 'isRejected',
        type: 'bool' as 'bool',
        canFilter: true
    }, {
        name: 'Antal användare',
        key: 'userCount',
        type: 'number' as 'number',
        canFilter: true
    }, {
        name: 'Fordonstyper',
        key: 'vehicleTypes',
        type: 'bool' as 'bool',
        getValue: (t: TenantForListType) => (t.vehicleTypes || []).length,
        isHidden: isCarrierFilterNo
    }, {
        name: 'Utrustning',
        key: 'equipment',
        type: 'bool' as 'bool',
        getValue: (t: TenantForListType) => (t.equipment || []).length,
        isHidden: isCarrierFilterNo
    }, {
        name: 'Hållbarhet',
        key: 'sustainability',
        type: 'bool' as 'bool',
        getValue: (t: TenantForListType) => (t.sustainability || []).length,
        isHidden: isCarrierFilterNo
    }, {
        name: 'Licenser/Certifikat',
        key: 'licencesCertificates',
        type: 'bool' as 'bool',
        getValue: (t: TenantForListType) => (t.licencesCertificates || []).length,
        isHidden: isCarrierFilterNo
    }, {
        name: 'Abonnemang',
        key: 'subscriptionEndDate',
        whiteSpace: 'pre-line' as const,
        getValue: (t: TenantForListType) => formatSubscriptionDate({
            endDate: t.subscriptionEndDate,
            isActive: t.activeSubscription,
            isCarrier: t.isCarrier
        })
    }, {
        name: 'Reg. datum',
        key: 'regDate',
        type: 'datetime' as 'datetime',
        canFilter: true
    }, {
        name: 'Senast aktiv',
        key: 'activeAt',
        type: 'datetime' as 'datetime',
        canFilter: true
    }, {
        name: 'Medlem i Fair Transport',
        key: 'fairtransportMembership',
        type: 'bool' as 'bool',
        getValue: (t: TenantForListType) => isFairtransportMember(t.fairtransportMembership),
        canFilter: !isCarrierFilterNo,
        isHidden: true
    }, {
        name: 'Hållbarhet',
        key: 'sustainability',
        type: 'multi-select' as 'multi-select',
        getValue: (t: TenantForListType) => (t.sustainability || []),
        options: (settableMetadata.sustainability || []),
        canFilter: !isCarrierFilterNo,
        isHidden: true
    }, {
        name: 'Fordonstyper',
        key: 'vehicleTypes',
        type: 'multi-select' as 'multi-select',
        getValue: (t: TenantForListType) => (t.vehicleTypes || []),
        options: (settableMetadata.vehicleTypes || []),
        canFilter: !isCarrierFilterNo,
        isHidden: true
    }, {
        name: 'Utrustning',
        key: 'equipment',
        type: 'multi-select' as 'multi-select',
        getValue: (t: TenantForListType) => (t.equipment || []),
        options: (settableMetadata.equipment || []),
        canFilter: !isCarrierFilterNo,
        isHidden: true
    }, {
        name: 'Licenser och certifikat',
        key: 'licencesCertificates',
        type: 'multi-select' as 'multi-select',
        getValue: (t: TenantForListType) => (t.licencesCertificates || []),
        options: (settableMetadata.licencesCertificates || []),
        canFilter: !isCarrierFilterNo,
        isHidden: true
    }];

    return (
        <Container>
            <Header
                title="Företag"
                addButton={{ url: '/tenants/create', label: 'Skapa nytt' }}
            />

            <Paper padding={0}>
                <Table
                    name="tenants"
                    columns={columns}
                    data={tenants}
                    onRowClick={onRowClick}
                    emptyText={isFetching ? 'Hämtar företag...' : 'Det finns inga företag.'}
                    showPagination
                    defaultOrderBy="regDate"
                    defaultOrderDirection="asc"
                />
            </Paper>
        </Container>
    );
}
